import React, { useCallback, useEffect, useState } from 'react';
import {
  getTeamLeaderGradeTrackSectionsPreview,
  resolveLeaderPreviewTrackMeta,
  type LeaderPreviewGradeSlot,
  type LeaderPreviewTrackColumnMeta,
  type LeaderPreviewTrackRow,
  type TeamLeaderGradeTrackSectionPreviewRow,
} from '../../services/gradeTrackTeamLeaderImportService';
import type { GradeTrackProgramPreferences } from '../../services/sectionLayoutService';
import type { TrackProgramMode } from '../../utils/trackProgramModeStorage';
import { sanitizeWildcardChar } from '../../utils/trackWildcard';

export type GradeTrackLeaderImportLocalPayload = {
  sourceSection: TeamLeaderGradeTrackSectionPreviewRow;
  programPreferences: GradeTrackProgramPreferences;
};

interface GradeTrackLeaderSectionImportModalProps {
  open: boolean;
  teamId: string;
  targetWorkspaceId: string;
  targetSectionId: string;
  onClose: () => void;
  onImported: (payload: GradeTrackLeaderImportLocalPayload) => void | Promise<void>;
}

function trackCategoryLabel(cat: string): string {
  return cat === 'other' ? '기타' : '정규';
}

function programModeLabel(mode: TrackProgramMode): string {
  if (mode === 'split') return '시간표별';
  if (mode === 'sync') return '동기화';
  return '편제별';
}

/** `GradeTrackPage.parseClassName`과 동일: `이름::1,2` → 제목 + 학년 칩 */
function parseLeaderClassName(raw: string): { title: string; grades: number[] } {
  const [title, rest] = raw.split('::');
  if (!rest) return { title: raw, grades: [] };
  const grades = rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
  return { title, grades };
}

type GroupedTrackColumn = {
  name: string;
  criteria_hours: number;
  track_category: string;
  aliases: string[];
};

function groupedTrackColumns(tracks: LeaderPreviewTrackRow[]): GroupedTrackColumn[] {
  const map = new Map<string, GroupedTrackColumn>();
  const order: string[] = [];
  tracks.forEach((track) => {
    const name = track.name.trim() || '(편제명 없음)';
    const alias = track.description.trim() || name;
    let current = map.get(name);
    if (!current) {
      current = {
        name,
        criteria_hours: track.criteria_hours,
        track_category: track.track_category,
        aliases: [],
      };
      map.set(name, current);
      order.push(name);
    }
    if (!current.aliases.includes(alias)) current.aliases.push(alias);
    current.criteria_hours = track.criteria_hours;
    current.track_category = track.track_category;
  });
  return order.map((name) => map.get(name)!);
}

function TrackAliasDefaultBadge({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <span
      className="track-alias-default-badge"
      title="와일드카드(괄호)로 입력된 시간표는 이 기본 시간표명으로 시수 집계·프로그램 구성됩니다"
    >
      기본
    </span>
  );
}

function LeaderPreviewTrackColumn({
  column,
  wildcardChar,
  programMode,
  syncRepAlias,
}: {
  column: GroupedTrackColumn;
  wildcardChar: string;
  programMode: TrackProgramMode;
  syncRepAlias: string;
}) {
  const displayAliases = column.aliases.length ? column.aliases : [column.name];
  const sanitizedWildcard = sanitizeWildcardChar(wildcardChar);

  return (
    <div className="track-col cp-grade-track-leader-import-track-col-readonly">
      <div className="track-col-header">
        <div className="track-header-top">
          <div className="track-header-actions">
            <button type="button" className="track-icon-button" disabled tabIndex={-1} aria-hidden>
              <img src="/edit.svg" alt="" className="track-icon" />
            </button>
            <button type="button" className="track-icon-button" disabled tabIndex={-1} aria-hidden>
              <img src="/Trash.svg" alt="" className="track-icon" />
            </button>
          </div>
        </div>
        <div className="track-category-toggle-row">
          <button
            type="button"
            className={'track-category-toggle-badge' + (column.track_category === 'other' ? ' is-other' : '')}
            disabled
            tabIndex={-1}
          >
            {trackCategoryLabel(column.track_category)}
          </button>
        </div>
        <div className="track-col-name">{column.name}</div>
      </div>
      <div className="track-col-wildcard">
        <span className="track-col-wildcard-label">(</span>
        <input
          className="track-col-wildcard-input"
          value={sanitizedWildcard}
          disabled
          readOnly
          tabIndex={-1}
          aria-hidden
        />
        <span className="track-col-wildcard-label">)</span>
      </div>
      <div className="track-col-body">
        {displayAliases.map((alias, index) =>
          programMode === 'sync' ? (
            <div
              key={`${column.name}-${alias}-${index}`}
              className={
                'track-alias track-alias-sync-select' +
                (alias === syncRepAlias ? ' is-sync-rep' : ' is-sync-non-rep')
              }
            >
              <span className="track-alias-sync-select-label">{alias || '\u00A0'}</span>
              {alias === syncRepAlias ? (
                <span className="cp-class-representative-badge track-alias-rep-badge">대표</span>
              ) : null}
              <TrackAliasDefaultBadge visible={index === 0 && !!sanitizedWildcard} />
            </div>
          ) : (
            <div key={`${column.name}-${alias}-${index}`} className="track-alias">
              {alias || '\u00A0'}
              <TrackAliasDefaultBadge visible={index === 0 && !!sanitizedWildcard} />
            </div>
          )
        )}
        {displayAliases.length >= 2 ? (
          <div className="track-split-mode-row">
            <span className="track-split-mode-title">프로그램</span>
            <span className="track-split-mode-title">생성 방법</span>
            <button
              type="button"
              className={
                'track-split-single-toggle-btn ' +
                (programMode !== 'unified' ? 'is-on' : 'is-off') +
                (programMode === 'sync' ? ' is-sync' : '')
              }
              disabled
              tabIndex={-1}
            >
              {programModeLabel(programMode)}
            </button>
          </div>
        ) : null}
      </div>
      <div className="track-col-criteria">
        <span className="track-col-criteria-label">기준시수</span>
        <input
          className="track-col-criteria-input"
          value={String(column.criteria_hours)}
          disabled
          readOnly
          tabIndex={-1}
        />
      </div>
    </div>
  );
}

function LeaderPreviewTrackTable({
  tracks,
  trackColumns,
  trackMeta,
  programPreferences,
  gradeId,
}: {
  tracks: LeaderPreviewTrackRow[];
  trackColumns: LeaderPreviewTrackColumnMeta[];
  trackMeta: LeaderPreviewGradeSlot['track_meta'];
  programPreferences: GradeTrackProgramPreferences;
  gradeId?: string;
}) {
  const columns = groupedTrackColumns(tracks);
  if (columns.length === 0) {
    return <p className="cp-grade-track-leader-import-empty-tracks">이 칸에 등록된 편제 행이 없습니다.</p>;
  }
  return (
    <div className="track-table">
      {columns.map((column, ci) => {
        const displayAliases = column.aliases.length ? column.aliases : [column.name];
        const resolved = resolveLeaderPreviewTrackMeta(column.name, displayAliases, {
          slotMeta: trackMeta[column.name.trim()],
          trackColumns,
          programPreferences,
          gradeId,
        });
        return (
          <LeaderPreviewTrackColumn
            key={`${column.name}-${ci}`}
            column={column}
            wildcardChar={resolved.wildcardChar}
            programMode={resolved.programMode}
            syncRepAlias={resolved.syncRepAlias}
          />
        );
      })}
    </div>
  );
}

function LeaderSectionPreviewBlock({
  section,
  programPreferences,
}: {
  section: TeamLeaderGradeTrackSectionPreviewRow;
  programPreferences: GradeTrackProgramPreferences;
}) {
  const slots: LeaderPreviewGradeSlot[] =
    section.grade_slots.length > 0
      ? section.grade_slots
      : section.grade_labels.map((label) => ({
          label,
          tracks: [] as LeaderPreviewTrackRow[],
          track_meta: {},
        }));

  return (
    <article className="cp-card cp-grade-track-leader-import-section-block">
      <div className="cp-track-section-header cp-track-section-header-with-class cp-grade-track-header-row">
        <div className="card-summary pill cp-track-class-pill">클래스 단독</div>
        <h3>편제 및 시간표 입력 양식</h3>
        {slots.length === 0 ? (
          <span className="cp-section-class-empty-hint">* 팀장 섹션에 연결된 클래스가 없습니다.</span>
        ) : (
          slots.map((slot, si) => {
            const parsed = parseLeaderClassName((slot.label ?? '').trim() || '(클래스 없음)');
            return (
              <div key={`${section.section_id}-${si}`} className="cp-grade-track-class-block">
                <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                {parsed.grades.map((grade) => (
                  <span key={grade} className="class-grade-chip-display">
                    {grade}
                  </span>
                ))}
              </div>
            );
          })
        )}
      </div>
      <div className="cp-section-body cp-grade-track-leader-import-section-body">
        {!section.detail_loaded && section.track_row_count > 0 ? (
          <p className="cp-grade-track-leader-import-preview-warning">
            팀장 편제 상세를 불러오려면 최신 DB 마이그레이션이 필요합니다. 적용 후에는 실제 편제 표가 이곳에 표시됩니다.
          </p>
        ) : null}
        {slots.map((slot, si) => (
          <div key={`${section.section_id}-slot-${si}`} className="cp-grade-track-leader-import-slot-panel">
            {slots.length > 1 ? (
              <div className="cp-grade-track-leader-import-slot-label">
                {(() => {
                  const parsed = parseLeaderClassName((slot.label ?? '').trim() || `클래스 ${si + 1}`);
                  return (
                    <>
                      <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                      {parsed.grades.map((grade) => (
                        <span key={grade} className="class-grade-chip-display">
                          {grade}
                        </span>
                      ))}
                    </>
                  );
                })()}
              </div>
            ) : null}
            <LeaderPreviewTrackTable
              tracks={slot.tracks}
              trackColumns={section.track_columns}
              trackMeta={slot.track_meta ?? {}}
              programPreferences={programPreferences}
              gradeId={slot.grade_id}
            />
          </div>
        ))}
      </div>
    </article>
  );
}

const GradeTrackLeaderSectionImportModal: React.FC<GradeTrackLeaderSectionImportModalProps> = ({
  open,
  teamId,
  onClose,
  onImported,
}) => {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<TeamLeaderGradeTrackSectionPreviewRow[]>([]);
  const [programPreferences, setProgramPreferences] = useState<GradeTrackProgramPreferences>({
    modes: {},
    syncReps: {},
    hoursAdjust: {},
    learningPublishers: {},
  });
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open) setActiveIndex(0);
  }, [open]);

  useEffect(() => {
    setActiveIndex((i) => {
      if (sections.length === 0) return 0;
      return Math.min(Math.max(0, i), sections.length - 1);
    });
  }, [sections]);

  const loadPreview = useCallback(async () => {
    if (!open || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await getTeamLeaderGradeTrackSectionsPreview(teamId);
      // 클래스가 있는 섹션만 (구 base 빈 섹션·서버 미필터 대비)
      const usable = payload.sections.filter(
        (s) => s.grade_slots.length > 0 || s.grade_labels.length > 0 || s.track_row_count > 0,
      );
      setSections(usable.length > 0 ? usable : payload.sections);
      setProgramPreferences(payload.program_preferences);
      setActiveIndex(0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [open, teamId]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const selectedSection = sections[activeIndex] ?? null;
  const selectedId = selectedSection?.section_id ?? null;
  const selectedNeedsUpdatedRpc = Boolean(
    selectedSection && !selectedSection.detail_loaded && selectedSection.track_row_count > 0
  );
  const sectionCount = sections.length;
  const navDisabled = sectionCount <= 1;

  const handleImport = async () => {
    if (!selectedSection || selectedNeedsUpdatedRpc) return;
    setImporting(true);
    setError(null);
    try {
      /** DB 즉시 저장 RPC 대신 미리보기 페이로드만 넘김 — 페이지에서 저장하기로 반영 */
      await onImported({
        sourceSection: selectedSection,
        programPreferences,
      });
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="cp-modal-backdrop cp-grade-track-leader-import-backdrop"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="cp-modal cp-grade-track-leader-import-modal cp-grade-track-leader-import-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-grade-track-leader-import-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cp-leader-import-modal-title-row">
          <button
            type="button"
            className="cp-leader-import-modal-nav-btn"
            aria-label="이전 섹션"
            disabled={navDisabled || activeIndex <= 0}
            onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
          >
            &lt;
          </button>
          <h3 id="cp-grade-track-leader-import-title" className="cp-leader-import-modal-title-text">
            팀장 편제 가져오기
          </h3>
          <button
            type="button"
            className="cp-leader-import-modal-nav-btn"
            aria-label="다음 섹션"
            disabled={navDisabled || activeIndex >= sectionCount - 1}
            onClick={() => setActiveIndex((i) => Math.min(sectionCount - 1, i + 1))}
          >
            &gt;
          </button>
        </div>
        <p className="page-desc cp-grade-track-leader-import-intro">
          아래는 팀장 워크스페이스의 섹션 블럭과 편제 표입니다. 타이틀 양쪽 화살표로 가져올 섹션을 고른 뒤 <strong>가져오기</strong>를 누르세요.
          화면에는 바로 반영되며, 오른쪽 위 &quot;저장하기&quot;를 눌러야 서버에 저장됩니다.
          <br />
          <span className="cp-grade-track-leader-import-slot-note">
            현재 내 섹션에 연결된 클래스 <strong>순서</strong>대로, 팀장 섹션의 1번째·2번째… 클래스 편제 설정(프로그램 생성 방식 등)이 각각 반영됩니다. 클래스 이름이 달라도 됩니다. 팀장 쪽 클래스 칸이 더 많으면
            뒤쪽 칸은 반영되지 않고, 내 쪽이 더 많으면 남는 내 클래스의 편제는 그대로 둡니다.
          </span>
        </p>
        {error ? <p className="cp-grade-track-leader-import-error">{error}</p> : null}
        <div className="cp-grade-track-leader-import-body">
          {loading ? (
            <p className="page-desc">불러오는 중…</p>
          ) : sections.length === 0 ? (
            <p className="page-desc">팀장 쪽에 표시할 섹션이 없습니다.</p>
          ) : (
            <div className="cp-grade-track-leader-import-section-grid cp-leader-import-section-single">
              {sections[activeIndex] ? (
                <LeaderSectionPreviewBlock
                  key={sections[activeIndex].section_id}
                  section={sections[activeIndex]}
                  programPreferences={programPreferences}
                />
              ) : null}
            </div>
          )}
        </div>
        <div className="cp-modal-footer">
          {selectedNeedsUpdatedRpc ? (
            <span className="cp-grade-track-leader-import-footer-warning">
              최신 DB 마이그레이션 적용 후 가져올 수 있습니다.
            </span>
          ) : null}
          <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={importing}>
            닫기
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={() => void handleImport()}
            disabled={!selectedId || selectedNeedsUpdatedRpc || importing || loading}
          >
            {importing ? '가져오는 중…' : '가져오기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GradeTrackLeaderSectionImportModal;
